import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { buildPpfPricingInput, pricePpfLine } from '@wrapforce/shared';
import type { InventoryItem, PricingResult, SizeClass } from '@wrapforce/shared';
import type { PpfMaterialTier, PpfZonePriceOverride } from '@wrapforce/shared/schemas/pricing';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/input';
import type { DraftLine } from '../NewQuotePage';
import { usePpfPricingProfile, useVehicleCatalogTrimDetail, useWorkspaceVehicleById } from './pricingApi';
import { usePpfZoneDefs } from '@/features/settings/pricing-v2/api';
import { useBrands, useInventory } from '@/features/inventory/api';
import {
  VehiclePicker, vehicleSelectionFromTrimDetail, vehicleSelectionFromWorkspaceVehicleItem,
  type CustomerVehicleHint, type VehicleSelection,
} from './VehiclePicker';
import { PricingBreakdownSidebar } from './PricingBreakdownSidebar';
import { AddInventoryItemDialog } from './AddInventoryItemDialog';

type PpfZoneKey = PpfZonePriceOverride['zoneKey'];
type PpfFilmTier = PpfMaterialTier['filmTier'];

const PPF_ZONE_KEYS: PpfZoneKey[] = ['partial_front', 'full_front', 'track_pack', 'full_body', 'custom_panels'];

// Translation keys for each zone id (not literal labels, since this module
// has no access to react-i18next's t() at module scope) - consumed both by
// the <option> labels below and by NewQuotePage's line summary.
export const PPF_ZONE_LABEL_KEYS: Record<PpfZoneKey, string> = {
  partial_front: 'pricing.ppf.zone.partial_front',
  full_front: 'pricing.ppf.zone.full_front',
  track_pack: 'pricing.ppf.zone.track_pack',
  full_body: 'pricing.ppf.zone.full_body',
  custom_panels: 'pricing.ppf.zone.custom_panels',
};

// Kept only for NewQuotePage's line-summary subtitle, which still needs to
// label a legacy saved line's filmTier (this builder no longer writes one).
export const PPF_FILM_TIER_LABEL_KEYS: Record<PpfFilmTier, string> = {
  standard: 'pricing.ppf.filmTier.standard',
  premium_self_healing: 'pricing.ppf.filmTier.premium_self_healing',
  stealth_matte: 'pricing.ppf.filmTier.stealth_matte',
  colored_ppf: 'pricing.ppf.filmTier.colored_ppf',
};

export interface PpfLineOutput {
  serviceKind: 'ppf';
  zoneKey: PpfZoneKey;
  sizeClass: SizeClass;
  /** Legacy fields - no longer set by this builder, kept optional for
   * back-compat with old saved lines (see NewQuotePage's subtitle/coverage
   * handling, which still reads them off a persisted DraftLine). */
  filmTier?: PpfFilmTier;
  ppfProductId?: string;
  estimatedMinutes: number;
  lineTotalCents: number;
  breakdown: Record<string, unknown>;
  profileVersionId: string;
  vehicleLabel: string;
  vehicleCatalogTrimId: string | null;
  workspaceVehicleId: string | null;
  materialInventoryItemId?: string | null;
  materialQty?: number | null;
  materialUnit?: 'sqft' | null;
  reservedMaterialCostCents?: number | null;
}

export interface PpfDraftState {
  vehicle: VehicleSelection | null;
  zoneKey: PpfZoneKey;
  materialItemId: string;
}

export function PpfLineBuilder({
  workspaceId, onAddLine, initialState, onStateChange, customerVehicleHint = null, customerId = null, editingLine = null, onCancelEdit,
}: {
  workspaceId: string | null;
  onAddLine: (line: PpfLineOutput) => void;
  // See VehicleAwareWrapLineBuilder: restore-on-mount + serialized-state emit
  // for the in-progress builder so leaving/reopening restores it open.
  initialState?: Record<string, unknown>;
  onStateChange?: (state: Record<string, unknown>) => void;
  customerVehicleHint?: CustomerVehicleHint | null;
  customerId?: string | null;
  editingLine?: DraftLine | null;
  onCancelEdit?: () => void;
}) {
  const { t } = useTranslation('quotes');
  const { data: profile } = usePpfPricingProfile(workspaceId);
  const { data: zoneDefs } = usePpfZoneDefs(workspaceId);

  // PPF film now comes from Inventory (category = 'ppf'), same as ceramic's
  // coating item picker - not a separate ppf-product catalog.
  const { data: inventoryData } = useInventory(workspaceId, { category: 'ppf' });
  const ppfItems = inventoryData?.items ?? [];
  const { data: brandsData } = useBrands(workspaceId);
  const brandNameById = new Map((brandsData?.items ?? []).map((b): [string, string] => [b.id, b.name]));
  const itemBrandName = (item: InventoryItem) => (item.brandId && brandNameById.get(item.brandId)) || t('pricing.noBrand');

  const editingCoverage = editingLine?.coverage?.serviceKind === 'ppf' ? editingLine.coverage : null;
  const restore = (editingLine ? undefined : initialState) as Partial<PpfDraftState> | undefined;

  const [vehicle, setVehicle] = useState<VehicleSelection | null>(restore?.vehicle ?? null);
  const [zoneKey, setZoneKey] = useState<PpfZoneKey>(editingCoverage?.zoneKey ?? restore?.zoneKey ?? 'full_front');
  const [materialItemId, setMaterialItemId] = useState<string>(editingLine?.materialInventoryItemId ?? restore?.materialItemId ?? '');
  const [addInventoryOpen, setAddInventoryOpen] = useState(false);
  // Tracks an item created via the inline dialog so the "default to first
  // item" effect below doesn't clobber the selection once useInventory
  // refetches and the new item appears in ppfItems.
  const [pendingSelectItemId, setPendingSelectItemId] = useState<string | null>(null);

  // See VehicleAwareWrapLineBuilder.tsx's identical effect for why: an edited
  // line only carries the vehicle's ids, not its full VehicleSelection.
  const { data: trimDetail } = useVehicleCatalogTrimDetail(workspaceId, editingLine?.vehicleCatalogTrimId ?? undefined);
  const { data: workspaceVehicleDetail } = useWorkspaceVehicleById(workspaceId, editingLine?.workspaceVehicleId ?? undefined);
  const resolvingEditVehicle = !!(editingLine?.vehicleCatalogTrimId || editingLine?.workspaceVehicleId) && !vehicle;

  useEffect(() => {
    if (vehicle) return;
    const resolved = trimDetail
      ? vehicleSelectionFromTrimDetail(trimDetail)
      : workspaceVehicleDetail
        ? vehicleSelectionFromWorkspaceVehicleItem(workspaceVehicleDetail)
        : null;
    if (resolved) setVehicle(resolved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimDetail, workspaceVehicleDetail]);

  const zoneDef = zoneDefs?.find((zd) => zd.zoneKey === zoneKey && zd.sizeClass === vehicle?.sizeClass);

  // Default to the first stocked brand/item once inventory loads, correcting
  // away from a selection that no longer exists (mirrors CeramicLineBuilder's
  // identical "keep the item selection valid" effect).
  useEffect(() => {
    if (!inventoryData) return;
    if (pendingSelectItemId && ppfItems.some((i) => i.id === pendingSelectItemId)) {
      setMaterialItemId(pendingSelectItemId);
      setPendingSelectItemId(null);
      return;
    }
    if (materialItemId && ppfItems.some((i) => i.id === materialItemId)) return;
    setMaterialItemId(ppfItems[0]?.id ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryData]);

  const selectedItem = ppfItems.find((i) => i.id === materialItemId);
  const selectedBrand = selectedItem ? itemBrandName(selectedItem) : '';
  const brandOptions = Array.from(new Set(ppfItems.map(itemBrandName))).sort();
  const itemOptionsForBrand = ppfItems.filter((i) => itemBrandName(i) === selectedBrand);

  function handleBrandChange(brand: string) {
    const firstMatch = ppfItems.find((i) => itemBrandName(i) === brand);
    setMaterialItemId(firstMatch?.id ?? '');
  }

  // Computed engine path: the zone def supplies the geometry (sqft/labor
  // minutes/cure hold), the selected inventory item supplies $/sqft - the
  // engine computes material + labor together, so no separate material cost
  // is added on top here (unlike ceramic/tint, which price labor only and
  // add material as an extra line).
  let result: PricingResult | null = null;
  if (zoneDef && vehicle && selectedItem && profile) {
    const input = buildPpfPricingInput({
      zoneSqftX10: zoneDef.sqftX10,
      zoneMinutes: zoneDef.minutes,
      cureHoldMinutes: zoneDef.cureHoldMinutes,
      costPerSqftCents: selectedItem.priceCentsPerUnit,
      designMinutes: 0,
      fixedAddOnCents: 0,
    });
    result = pricePpfLine(input, profile.resolved);
  }

  const canAdd = !!vehicle && !!zoneDef && !!result && !!profile && !!selectedItem;

  // Single source of truth for this line's output - see VehicleAwareWrapLineBuilder.
  const liveOutput: PpfLineOutput | null = canAdd && vehicle && zoneDef && result && profile && selectedItem
    ? {
        serviceKind: 'ppf',
        zoneKey,
        sizeClass: vehicle.sizeClass,
        estimatedMinutes: result.estimatedMinutes,
        lineTotalCents: result.lineSubtotalCents,
        breakdown: result.breakdown as unknown as Record<string, unknown>,
        profileVersionId: profile.versionId,
        vehicleLabel: vehicle.label,
        vehicleCatalogTrimId: vehicle.catalogTrimId,
        workspaceVehicleId: vehicle.workspaceVehicleId,
        materialInventoryItemId: selectedItem.id,
        materialQty: zoneDef.sqftX10 / 10,
        materialUnit: 'sqft',
        reservedMaterialCostCents: result.materialCostCents > 0 ? result.materialCostCents : null,
      }
    : null;

  // Persist raw builder state (add mode, vehicle picked) - see VehicleAwareWrapLineBuilder.
  const draftState: PpfDraftState = { vehicle, zoneKey, materialItemId };
  const draftStateKey = JSON.stringify(draftState);
  useEffect(() => {
    if (editingLine || !vehicle) return;
    onStateChange?.(draftState as unknown as Record<string, unknown>);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftStateKey, editingLine]);

  function handleAddLine() {
    if (!liveOutput) return;
    onAddLine(liveOutput);
    toast.success(editingLine ? t('pricing.lineUpdatedToast') : t('pricing.ppf.toastAdded'));

    if (!editingLine) {
      setVehicle(null);
      setZoneKey('full_front');
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <section className="flex flex-col gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-[.08em] text-muted">{t('pricing.step.vehicle')}</h3>
        {resolvingEditVehicle ? (
          <p className="text-sm text-muted">{t('pricing.loadingVehicle')}</p>
        ) : (
          <VehiclePicker workspaceId={workspaceId} selection={vehicle} onSelect={setVehicle} customerVehicleHint={customerVehicleHint} customerId={customerId} />
        )}
      </section>

      {vehicle ? (
        <>
          <section className="flex flex-col gap-2">
            <h3 className="text-xs font-semibold uppercase tracking-[.08em] text-muted">{t('pricing.step.ppfZoneTier')}</h3>
            <div className="flex flex-col gap-1">
              <Label htmlFor="ppf-zone">{t('pricing.ppf.zoneLabel')}</Label>
              <select
                id="ppf-zone"
                value={zoneKey}
                onChange={(e) => setZoneKey(e.target.value as PpfZoneKey)}
                className="h-9 w-full max-w-xs border border-border bg-surface px-2 text-sm"
              >
                {PPF_ZONE_KEYS.map((zk) => (
                  <option key={zk} value={zk}>{t(PPF_ZONE_LABEL_KEYS[zk])}</option>
                ))}
              </select>
            </div>
            {!zoneDef ? (
              <p className="text-sm text-muted">{t('pricing.ppf.noZoneData')}</p>
            ) : null}
          </section>

          <section className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-xs font-semibold uppercase tracking-[.08em] text-muted">{t('pricing.step.materialCoverage')}</h3>
              <button type="button" onClick={() => setAddInventoryOpen(true)} className="text-[11px] font-semibold text-accent-ink hover:underline">
                {t('pricing.material.addToInventory')}
              </button>
            </div>
            {!inventoryData ? (
              <p className="text-xs text-muted">{t('pricing.material.loadingStocked')}</p>
            ) : ppfItems.length === 0 ? (
              <div className="flex flex-col gap-1.5 border border-border p-3">
                <p className="text-sm text-muted">{t('pricing.ppf.noInventoryItems')}</p>
                <Link to="/inventory" className="w-fit text-xs font-semibold text-accent-ink underline">
                  {t('pricing.ppf.addToInventoryLink')}
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                <div className="flex flex-col gap-1">
                  <Label htmlFor="ppf-brand">{t('pricing.ppf.brandLabel')}</Label>
                  <select
                    id="ppf-brand"
                    value={selectedBrand}
                    onChange={(e) => handleBrandChange(e.target.value)}
                    className="h-9 border border-border bg-surface px-2 text-sm"
                  >
                    {brandOptions.map((b) => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-1">
                  <Label htmlFor="ppf-item">{t('pricing.ppf.itemLabel')}</Label>
                  <select
                    id="ppf-item"
                    value={materialItemId}
                    onChange={(e) => setMaterialItemId(e.target.value)}
                    className="h-9 border border-border bg-surface px-2 text-sm"
                  >
                    {itemOptionsForBrand.map((i) => (
                      <option key={i.id} value={i.id}>{i.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </section>

          {result ? <PricingBreakdownSidebar result={result} /> : null}

          <div className="flex items-center gap-2">
            <Button type="button" disabled={!canAdd} onClick={handleAddLine}>
              {editingLine ? t('pricing.saveChanges') : t('pricing.addService')}
            </Button>
            {editingLine ? (
              <Button type="button" variant="secondary" onClick={onCancelEdit}>
                {t('pricing.cancelEdit')}
              </Button>
            ) : null}
          </div>
        </>
      ) : (
        <p className="text-sm text-muted">{t('pricing.pickVehicle')}</p>
      )}

      <AddInventoryItemDialog
        open={addInventoryOpen}
        onOpenChange={setAddInventoryOpen}
        workspaceId={workspaceId}
        category="ppf"
        onCreated={(itemId) => {
          setPendingSelectItemId(itemId);
          setMaterialItemId(itemId);
        }}
      />
    </div>
  );
}
